import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../axiosInstance";

export function useTeacherPortal(endpoint) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(endpoint);
      setData(response.data || {});
    } catch (error) {
      console.error(error);
      toast.error("Failed to load teacher portal data.");
      setData({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, [endpoint]);

  return { data, loading, reload, setData };
}

export function TeacherPageHeader({ title, subtitle, icon = "bi-person-workspace", actions }) {
  return (
    <div className="card border-0 shadow-sm mb-3">
      <div className="card-body d-flex flex-wrap justify-content-between align-items-center gap-3">
        <div className="d-flex align-items-center gap-3">
          <div className="rounded-3 p-3 text-success" style={{ background: "#eef8f1" }}>
            <i className={`bi ${icon} fs-3`}></i>
          </div>
          <div>
            <h4 className="fw-bold mb-1" style={{ color: "#125b2d" }}>{title}</h4>
            <p className="text-muted mb-0">{subtitle}</p>
          </div>
        </div>
        {actions && <div className="d-flex flex-wrap gap-2">{actions}</div>}
      </div>
    </div>
  );
}

export function PortalLoading({ label = "Loading..." }) {
  return <div className="text-center text-muted py-5"><div className="spinner-border text-success mb-3"></div><div>{label}</div></div>;
}

export function EmptyState({ title = "No records found", text = "There is nothing to show yet." }) {
  return <div className="text-center text-muted py-4"><i className="bi bi-inbox fs-1 d-block mb-2"></i><h6 className="fw-bold">{title}</h6><p className="mb-0">{text}</p></div>;
}

export function StatusBadge({ value }) {
  const normalized = String(value || "").toUpperCase();
  const success = ["ACTIVE", "OPEN", "PUBLISHED", "VERIFIED", "SUBMITTED", "SCHEDULED", "LIVE", "COMPLETED", "ALLOWED", "COMPETENT", "ADVANCED"];
  const warning = ["DRAFT", "PENDING", "PARTIALLY_PAID", "PLANNED", "DEVELOPING", "RESUBMIT"];
  const cls = success.includes(normalized) ? "bg-success" : warning.includes(normalized) ? "bg-warning text-dark" : "bg-secondary";
  return <span className={`badge ${cls}`}>{value || "-"}</span>;
}

export function SummaryCard({ label, value, icon = "bi-circle", tone = "success" }) {
  return (
    <div className="col-md-3 col-sm-6">
      <div className="card border-0 shadow-sm h-100">
        <div className="card-body d-flex justify-content-between align-items-center">
          <div><small className="text-muted fw-bold">{label}</small><h4 className="fw-bold mb-0">{value}</h4></div>
          <div className={`rounded-3 p-3 text-${tone}`} style={{ background: tone === "warning" ? "#fff8e6" : "#eef8f1" }}><i className={`bi ${icon} fs-4`}></i></div>
        </div>
      </div>
    </div>
  );
}

export function niceDate(value) {
  if (!value) return "-";
  try { return new Date(value).toLocaleDateString(); } catch { return value; }
}

export function niceDateTime(value) {
  if (!value) return "-";
  try { return new Date(value).toLocaleString(); } catch { return value; }
}

export function SelectField({ label, value, onChange, options, required = true }) {
  return (
    <div className="col-md-4">
      <label className="form-label fw-semibold">{label}</label>
      <select className="form-select" value={value || ""} onChange={(event) => onChange(event.target.value)} required={required}>
        <option value="">Select {label.toLowerCase()}</option>
        {(options || []).map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
      </select>
    </div>
  );
}

export function TextField({ label, value, onChange, type = "text", required = false, placeholder = "" }) {
  return (
    <div className="col-md-4">
      <label className="form-label fw-semibold">{label}</label>
      <input type={type} className="form-control" value={value || ""} placeholder={placeholder} onChange={(event) => onChange(event.target.value)} required={required} />
    </div>
  );
}

export function TextAreaField({ label, value, onChange, required = false }) {
  return (
    <div className="col-12">
      <label className="form-label fw-semibold">{label}</label>
      <textarea className="form-control" rows="3" value={value || ""} onChange={(event) => onChange(event.target.value)} required={required}></textarea>
    </div>
  );
}

export function SimpleTable({ headers, rows, emptyTitle = "No records found" }) {
  return (
    <div className="table-responsive border rounded-3">
      <table className="table table-hover align-middle mb-0">
        <thead className="table-light"><tr>{headers.map((header) => <th key={header}>{header}</th>)}</tr></thead>
        <tbody>
          {rows && rows.length ? rows.map((row, index) => <tr key={index}>{row.map((cell, c) => <td key={c}>{cell}</td>)}</tr>) : <tr><td colSpan={headers.length}><EmptyState title={emptyTitle} /></td></tr>}
        </tbody>
      </table>
    </div>
  );
}
